"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const SIGNUP = [
  { n: "1", title: "Register", desc: "Name, email, password" },
  { n: "2", title: "Verify OTP", desc: "Code from your inbox" },
  { n: "3", title: "Create company", desc: "Company name only — GSTIN, PAN, address later" },
  { n: "4", title: "Log in", desc: "Sign-up does not log you in. Use the same email and password" },
];

const TRIAL = [
  "Free plan on the company",
  "Document prefixes (INV-, BILL-, SO-, and the rest)",
  "Default Division, Branch, Warehouse named default",
  "Admin role with full access, assigned to you",
  "Chart of Accounts seeded",
];

const CHECKLIST = [
  {
    step: "Add Organisation Details",
    done: "GSTIN or PAN, base currency, and address (street, country, state, city) on Profile",
    where: "Settings → Profile",
  },
  {
    step: "Add your first item",
    done: "Any Item exists",
    where: "All Master → Item",
  },
  {
    step: "Add your first customer",
    done: "A party marked as customer",
    where: "Sales → Customer",
  },
  {
    step: "Setup banking",
    done: "An active bank account marked primary",
    where: "Banking → Banks",
  },
  {
    step: "Invite your team",
    done: "More than one user on the company",
    where: "Settings → Users",
  },
  {
    step: "Create your first invoice",
    done: "Any Invoice exists",
    where: "Sales → Invoices",
  },
  {
    step: "Add your first vendor",
    done: "A party marked as vendor",
    where: "Purchase → Vendors",
  },
  {
    step: "Create your first bill",
    done: "Any Bill exists",
    where: "Purchase → Bills",
  },
];

const sections = [
  { heading: "Sign up" },
  { heading: "Activate the free trial" },
  { heading: "Getting Started tab" },
];

export default function SettingUpPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Setting Up Your Account
          </h1>
          <h2 className="text-[20px] sentient-medium text-[#111827] mb-3">
            For: The first admin creating the company
          </h2>
          <p className="text-[16px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Do this at <B>accounts.abss.ai</B>, then open Books. Teammates you
            invite skip sign-up and land on login.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Sign up
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SIGNUP.map((s) => (
              <div
                key={s.n}
                className="border border-[#E5E5E5] rounded-[12px] p-4 bg-white flex gap-3"
              >
                <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0">
                  {s.n}
                </span>
                <div>
                  <p className="text-[14px] geist-medium text-[#111827]">
                    {s.title}
                  </p>
                  <p className="text-[13px] geist-regular text-[#374151]">
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <Callout type="note">
            OTP confirms the email. The company exists only after step 3.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            Activate the free trial
          </h2>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-4">
            Prompted after first login. Until this runs, adding an item,
            inviting a user, or creating an invoice is blocked.
          </p>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            {TRIAL.map((row) => (
              <p
                key={row}
                className="px-4 py-2.5 text-[14px] geist-regular text-[#374151] border-b border-[#E5E5E5] last:border-b-0"
              >
                {row}
              </p>
            ))}
          </div>
          <Callout type="warning">
            You land on <B>General Dashboard</B>, not a wizard. The eight
            steps live on Home → Getting Started.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-2"
            className="text-[24px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            Getting Started tab
          </h2>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-4">
            Any order. Ticks from live data — not a checkbox. Invite is
            Settings → Users (admin + active subscription).
          </p>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Step
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Done when
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Where
                  </th>
                </tr>
              </thead>
              <tbody>
                {CHECKLIST.map((row) => (
                  <tr
                    key={row.step}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.step}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151] align-top">
                      {row.done}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151] align-top">
                      {row.where}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/home/dashboard"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              Dashboard <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Getting Started tab on Home
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/profile"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              Profile <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Organisation details
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/users"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black mb-1 flex items-center gap-1.5">
              Users <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Invite after the trial is on
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
